/** @jsxImportSource @emotion/react */

import { formatErrorResponse } from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  useTheme,
  Input,
  CryptoCurrency,
  Select,
  Snackbar,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { useAddRecipient } from 'api/walletRecipientApi';
import { ContactStatus } from 'api/walletRecipientApi.types';
import { Layout, LayoutModal } from 'components';
import { useOrganisation } from 'contexts';
import { SupportedCorporateWalletCryptoCurrencies } from 'tabs/types';
import { isValidCryptoAddress } from 'utils';

export const AddRecipient = () => {
  const { spacing } = useTheme();
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  //Since we only have one supported currency(USDC) for now
  const [currency, setCurrency] = useState<CryptoCurrency>('USDC');
  const navigate = useNavigate();
  const {
    selectedMerchant: { merchantId },
  } = useOrganisation();
  const {
    mutateAsync: addRecipient,
    isPending: addRecipientLoading,
    error: addRecipientError,
  } = useAddRecipient(merchantId);

  const addRecipientHandler = async () => {
    const recipient = await addRecipient({
      address,
      label,
      currency,
    });
    if (recipient.status !== ContactStatus.ACTIVE && !recipient.isInternal) {
      void navigate(`travel-rule-info/${recipient.uuid}`);
    } else {
      void navigate('/wallet/recipients');
    }
  };

  const [showAddRecipientError, setShowAddRecipientError] = useState(false);

  useUpdateEffect(() => {
    setShowAddRecipientError(!!addRecipientError);
  }, [addRecipientError]);

  const invalidAddress = !address || !isValidCryptoAddress(address, currency);

  return (
    <LayoutModal onClose={() => navigate('/wallet/recipients')}>
      <Layout
        title="Add new wallet recipient"
        primaryButtonProps={{
          text: 'Add recipient',
          onClick: addRecipientHandler,
          disabled: invalidAddress || !label || addRecipientLoading,
          loading: addRecipientLoading,
        }}
        secondaryButtonProps={{
          text: 'Cancel',
          onClick: () => navigate('/wallet/recipients'),
        }}
      >
        <div
          css={{
            textAlign: 'center',
            margin: `${spacing.xxs} 0`,
            padding: `0 ${spacing.xxs}`,
          }}
        >
          <Typography variant="bodySmall">
            This will add a new wallet recipient. Someone from your organisation
            will have to approve it before it can be used to send.
          </Typography>
        </div>
        <div
          css={{
            display: 'grid',
            gap: spacing.xxs,
            padding: `0 ${spacing.xxs}`,
          }}
        >
          <Input
            value={address}
            label="Address"
            onChange={(e) => {
              setAddress(e.target.value);
            }}
            error={!!address && invalidAddress}
            helperText={
              !!address && invalidAddress
                ? 'Please enter a valid ERC-20 USDC address'
                : undefined
            }
          />
          <Input
            value={label}
            label="Label"
            onChange={(e) => {
              setLabel(e.target.value);
            }}
          />
          <Select
            options={SupportedCorporateWalletCryptoCurrencies}
            label="Currency"
            values={[currency]}
            onSelect={(data) => {
              setCurrency(data[0] as CryptoCurrency);
            }}
            disabled //Since we only have one supported currency(USDC) for now
          />
        </div>
      </Layout>
      {showAddRecipientError && addRecipientError && (
        <Snackbar
          message={formatErrorResponse(addRecipientError)}
          variant="danger"
          open={showAddRecipientError}
          onClose={() => {
            setShowAddRecipientError(false);
          }}
        />
      )}
    </LayoutModal>
  );
};
